/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useRef, useState } from "react";
import {
  HiOutlineCheck,
  HiOutlineClipboardCopy,
  HiOutlineExclamation,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineKey,
  HiOutlineLightBulb,
  HiOutlineLockClosed,
  HiOutlineRefresh,
  HiOutlineShieldCheck,
  HiOutlineX,
} from "react-icons/hi";

import { BreadCrumbComponent } from "../../ui/BreadCrumbComponent";
import { Avatar, Button, Card, PageHeader, cn } from "../../ui";
import { useAdminContext } from "../../context/admin/AdminContext";

type Banner = {
  tone: "success" | "error";
  message: string;
};

type Check = {
  key: "length" | "upper" | "lower" | "number" | "symbol";
  label: string;
  ok: boolean;
};

type Strength = {
  score: number; // 0-5
  label: string;
  checks: Check[];
};

const STRENGTH_LABELS = ["Very weak", "Weak", "Okay", "Good", "Strong", "Excellent"];

const strengthOf = (pwd: string): Strength => {
  const checks: Check[] = [
    { key: "length", label: "8+ characters", ok: pwd.length >= 8 },
    { key: "upper", label: "Uppercase letter", ok: /[A-Z]/.test(pwd) },
    { key: "lower", label: "Lowercase letter", ok: /[a-z]/.test(pwd) },
    { key: "number", label: "Number", ok: /\d/.test(pwd) },
    { key: "symbol", label: "Symbol", ok: /[^A-Za-z0-9]/.test(pwd) },
  ];
  const score = checks.reduce((acc, c) => acc + (c.ok ? 1 : 0), 0);
  return { score, label: STRENGTH_LABELS[score] ?? STRENGTH_LABELS[0], checks };
};

const SEGMENT_TONE: Record<number, string> = {
  0: "bg-gray-100",
  1: "bg-red-400",
  2: "bg-amber-400",
  3: "bg-amber-400",
  4: "bg-emerald-400",
  5: "bg-brand",
};

// Generates a cryptographically random password that satisfies every rule.
const generatePassword = (length = 16): string => {
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const symbols = "!@#$%^&*()-_=+[]{}";
  const all = lower + upper + digits + symbols;

  const pickFrom = (pool: string, n: number): string[] => {
    const out: string[] = [];
    const buf = new Uint32Array(n);
    if (typeof window !== "undefined" && window.crypto) {
      window.crypto.getRandomValues(buf);
    } else {
      for (let i = 0; i < n; i++) buf[i] = Math.floor(Math.random() * 0xffffffff);
    }
    for (let i = 0; i < n; i++) out.push(pool[buf[i] % pool.length]);
    return out;
  };

  const required = [
    ...pickFrom(lower, 1),
    ...pickFrom(upper, 1),
    ...pickFrom(digits, 1),
    ...pickFrom(symbols, 1),
  ];
  const rest = pickFrom(all, Math.max(0, length - required.length));
  const merged = [...required, ...rest];

  // Fisher-Yates shuffle for uniform distribution.
  for (let i = merged.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [merged[i], merged[j]] = [merged[j], merged[i]];
  }
  return merged.join("");
};

// ─── Field: password input with inline eye toggle ───────────────────
type FieldProps = {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  visible: boolean;
  onToggleVisible: () => void;
  onChange: (next: string) => void;
  trailing?: React.ReactNode;
  autoComplete?: string;
  autoFocus?: boolean;
};

const PasswordField = React.forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      id,
      label,
      value,
      placeholder,
      visible,
      onToggleVisible,
      onChange,
      trailing,
      autoComplete,
      autoFocus,
    },
    ref,
  ) => (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block text-[12px] font-semibold tracking-tight text-gray-800"
      >
        {label}
      </label>
      <div className="relative">
        <input
          ref={ref}
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-24 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <HiOutlineLockClosed className="h-4 w-4" />
        </span>
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {trailing}
          <button
            type="button"
            onClick={onToggleVisible}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-brand/5 hover:text-brand"
            aria-label={visible ? "Hide password" : "Show password"}
          >
            {visible ? (
              <HiOutlineEyeOff className="h-4 w-4" />
            ) : (
              <HiOutlineEye className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  ),
);
PasswordField.displayName = "PasswordField";

// ─── Strength meter ─────────────────────────────────────────────────
const StrengthMeter: React.FC<{ strength: Strength; empty: boolean }> = ({
  strength,
  empty,
}) => {
  const activeTone = SEGMENT_TONE[strength.score];
  return (
    <div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-semibold uppercase tracking-[0.1em] text-gray-500">
          Strength
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors",
            empty
              ? "bg-gray-100 text-gray-500"
              : strength.score >= 4
                ? "bg-brand/10 text-brand ring-1 ring-inset ring-brand/15"
                : strength.score >= 2
                  ? "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200"
                  : "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200",
          )}
        >
          {empty ? "Start typing…" : strength.label}
        </span>
      </div>
      <div className="mt-2 flex gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            aria-hidden
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors duration-200",
              i < strength.score ? activeTone : "bg-gray-100",
            )}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Check pill ─────────────────────────────────────────────────────
const CheckPill: React.FC<{ check: Check }> = ({ check }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium transition-colors",
      check.ok
        ? "bg-brand/10 text-brand ring-1 ring-inset ring-brand/15"
        : "bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200",
    )}
  >
    {check.ok ? (
      <HiOutlineCheck className="h-3.5 w-3.5" />
    ) : (
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
    )}
    {check.label}
  </span>
);

// ─── Banner ─────────────────────────────────────────────────────────
const BannerView: React.FC<{ banner: Banner; onDismiss: () => void }> = ({
  banner,
  onDismiss,
}) => (
  <div
    role="status"
    aria-live="polite"
    className={cn(
      "flex items-start gap-3 rounded-2xl border p-4 text-[13px]",
      banner.tone === "success"
        ? "border-brand/20 bg-brand/5 text-brand"
        : "border-red-200 bg-red-50 text-red-700",
    )}
  >
    <span
      aria-hidden
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
        banner.tone === "success"
          ? "bg-brand text-white"
          : "bg-red-600 text-white",
      )}
    >
      {banner.tone === "success" ? (
        <HiOutlineShieldCheck className="h-4 w-4" />
      ) : (
        <HiOutlineExclamation className="h-4 w-4" />
      )}
    </span>
    <div className="min-w-0 flex-1 pt-1 font-medium">{banner.message}</div>
    <button
      type="button"
      onClick={onDismiss}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-current opacity-70 transition-opacity hover:opacity-100"
      aria-label="Dismiss"
    >
      <HiOutlineX className="h-4 w-4" />
    </button>
  </div>
);

// ─── Main page ──────────────────────────────────────────────────────
const Overview: React.FC = () => {
  const { sysAdmin, updateAdminPassword, logOut } =
    useAdminContext() as any;

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [copied, setCopied] = useState(false);

  const newRef = useRef<HTMLInputElement>(null);

  const fullName =
    `${(sysAdmin as any)?.first_name ?? ""} ${(sysAdmin as any)?.last_name ?? ""}`
      .trim() || "Admin";
  const email = (sysAdmin as any)?.email ?? "";
  const role = (sysAdmin as any)?.role ?? "";

  const strength = useMemo(() => strengthOf(password), [password]);
  const matches = useMemo(() => {
    if (!password && !confirmPassword) return null;
    return password === confirmPassword;
  }, [password, confirmPassword]);

  const isStrongEnough = strength.score >= 4;
  const canSubmit = isStrongEnough && matches === true && !loading;

  const handleGenerate = async () => {
    const next = generatePassword(16);
    setPassword(next);
    setConfirmPassword(next);
    setShowPwd(true);
    setShowConfirmPwd(true);
    newRef.current?.focus();
    // Best-effort copy to clipboard so the admin can stash it right away.
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(next);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        /* clipboard unavailable — ignore. */
      }
    }
  };

  const handleCopy = async () => {
    if (!password) return;
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(password);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        /* ignore */
      }
    }
  };

  const handleReset = () => {
    setPassword("");
    setConfirmPassword("");
    setShowPwd(false);
    setShowConfirmPwd(false);
    setBanner(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setBanner(null);
    const { status } = await updateAdminPassword({ password });
    if (status === 200) {
      setBanner({
        tone: "success",
        message:
          "Password updated. You'll be signed out in a moment — sign back in with your new password.",
      });
      window.setTimeout(() => logOut(), 1500);
    } else {
      setBanner({
        tone: "error",
        message: "Couldn't update your password. Please try again.",
      });
      setLoading(false);
    }
  };

  return (
    <div className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-gray-50">
      <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-6 sm:px-6">
        <BreadCrumbComponent />

        <PageHeader
          title="Security"
          subtitle="Rotate your dashboard password. You'll be signed out once it's updated."
          icon={<HiOutlineShieldCheck className="h-5 w-5" />}
        />

        {/* Identity hero */}
        <section className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-brand/10 blur-3xl"
          />
          <div className="relative flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <div className="flex items-center gap-3">
              <Avatar
                firstName={(sysAdmin as any)?.first_name}
                lastName={(sysAdmin as any)?.last_name}
                size="lg"
              />
              <div className="min-w-0">
                <p className="truncate text-[17px] font-semibold tracking-tight text-gray-900">
                  {fullName}
                </p>
                {email ? (
                  <p className="truncate text-[12.5px] text-gray-500">
                    {email}
                  </p>
                ) : null}
              </div>
            </div>

            {role ? (
              <span className="inline-flex items-center gap-2 self-start rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand sm:self-auto">
                <HiOutlineKey className="h-3.5 w-3.5" />
                {role.replace("_", " ")}
              </span>
            ) : null}
          </div>
        </section>

        {banner ? (
          <BannerView banner={banner} onDismiss={() => setBanner(null)} />
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <Card padding="md" className="space-y-5">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-[14px] font-semibold tracking-tight text-gray-900">
                Update your password
              </h2>
              <button
                type="button"
                onClick={handleGenerate}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-700 transition-colors hover:border-brand/30 hover:bg-brand/5 hover:text-brand"
              >
                <HiOutlineRefresh className="h-3.5 w-3.5" />
                Generate strong password
              </button>
            </div>

            <PasswordField
              ref={newRef}
              id="new-password"
              label="New password"
              value={password}
              placeholder="At least 8 characters"
              visible={showPwd}
              onToggleVisible={() => setShowPwd((v) => !v)}
              onChange={setPassword}
              autoComplete="new-password"
              autoFocus
              trailing={
                password ? (
                  <button
                    type="button"
                    onClick={handleCopy}
                    className={cn(
                      "inline-flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] font-semibold transition-colors",
                      copied
                        ? "bg-brand/10 text-brand ring-1 ring-inset ring-brand/15"
                        : "text-gray-500 hover:bg-brand/5 hover:text-brand",
                    )}
                    title="Copy to clipboard"
                    aria-label="Copy password"
                  >
                    {copied ? (
                      <HiOutlineCheck className="h-3.5 w-3.5" />
                    ) : (
                      <HiOutlineClipboardCopy className="h-3.5 w-3.5" />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </button>
                ) : null
              }
            />

            <StrengthMeter strength={strength} empty={!password} />

            <div className="flex flex-wrap gap-1.5">
              {strength.checks.map((check) => (
                <CheckPill key={check.key} check={check} />
              ))}
            </div>

            <PasswordField
              id="confirm-password"
              label="Confirm password"
              value={confirmPassword}
              placeholder="Re-enter password"
              visible={showConfirmPwd}
              onToggleVisible={() => setShowConfirmPwd((v) => !v)}
              onChange={setConfirmPassword}
              autoComplete="new-password"
            />

            {matches !== null ? (
              <p
                className={cn(
                  "inline-flex items-center gap-1.5 text-[12px] font-medium",
                  matches ? "text-brand" : "text-red-600",
                )}
                role="status"
                aria-live="polite"
              >
                {matches ? (
                  <HiOutlineCheck className="h-3.5 w-3.5" />
                ) : (
                  <HiOutlineX className="h-3.5 w-3.5" />
                )}
                {matches ? "Passwords match" : "Passwords don't match yet"}
              </p>
            ) : null}

            <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[11.5px] text-gray-500">
                Use a mix of characters — avoid reusing anything from other sites.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleReset}
                  disabled={!password && !confirmPassword}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!canSubmit}
                >
                  {loading ? "Updating…" : "Update password"}
                </Button>
              </div>
            </div>
          </Card>
        </form>

        {/* Tip card */}
        <Card padding="md">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
              <HiOutlineLightBulb className="h-4 w-4" />
            </span>
            <div>
              <p className="text-[13px] font-semibold tracking-tight text-gray-900">
                Tips for a strong password
              </p>
              <ul className="mt-1.5 space-y-1 text-[12px] text-gray-600">
                <li>
                  Use at least 12 characters — longer is stronger.
                </li>
                <li>
                  Mix uppercase, lowercase, numbers, and symbols.
                </li>
                <li>
                  Never reuse a password you've used on another site.
                </li>
                <li>
                  Store it in a password manager so you don't have to remember it.
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Overview;
